/* eslint-disable max-len */
import { config } from 'config';
import { BASE_URL } from './constants';
import { flattenObjectToStringList } from './helpers';

export const V1 = `${BASE_URL}/api`;

export const PUBLIC_URLS = {
    SIGN_UP: {
        LANDING_PAGE: '/signup',
        DIRECT_SIGN_UP: '/signup/direct',
    },
};

export const PUBLIC_URLS_LIST = flattenObjectToStringList(PUBLIC_URLS);

export const URLS = {
    // Cloudfront url for images
    IMAGE_CLOUDFRONT_URL: (name: string) => `https://d24iv9sojvq5iq.cloudfront.net/images/${name}`,
    ACCOUNT_CREATED: '/account_created',
    REPORTS: {
        HOME: '/reports',
        NEW: '/reports/new/:reportType',
        REPORT_PAGE: '/reports/:reportId',
        REPORT_PREVIEW: '/reports/:reportId/preview',
    },
    INVESTMENT_MEMO: '/investment-memo',
    DEAL_INVESTMENT_MEMO: '/deal/:dealId/investment-memo',
    REPORT_BUILDER: '/deal/:dealId/report_builder',
    DEALS: {
        HOME: '/deals',
        VIEW: '/deals/:dealId',
        FORM: '/deals/form/:dealId?',
        COMPARISON: '/deals/comparison/:dealId?',
        BULK_UPLOAD: '/deals/bulk-upload',
        PRO_FORMAS: '/deals/pro-formas',
    },
    DEAL_DASHBOARD: {
        HOME: '/deal/:dealId/:tab?',
    },
    PRO_FORMA: {
        HOME: '/deal/:dealId/pro_forma',
        BASE_URL: '/deal/:dealId/pro_forma/:proFormaId',
        TABLE: '/deal/:dealId/pro_forma/:proFormaId/table',
        UPLOAD: '/deal/:dealId/pro_forma/upload/:proFormaId?',
    },
    TASKS:{
        HOME: '/deal/:dealId/tasks',
    },
    DOCUMENTS: '/deal/:dealId/documents',

    // Project App URLs
    PROJECTS_APP: {
        APPROVALS: `${config.northspyreProjectsAppUrl}/ns/v2/portfolio_approvals`,
        PROJECTS: `${config.northspyreProjectsAppUrl}/ns/v2/projects`,
        ORGANIZATION_MANAGEMENT: `${config.northspyreProjectsAppUrl}/ns/v2/organizations`,
        USER_SETTINGS: `${config.northspyreProjectsAppUrl}/ns/v2/profile/user`,
        COST_ANALYTICS: `${config.northspyreProjectsAppUrl}/ns/v2/cost_analytics`,
        MARKET_DATA: `${config.northspyreProjectsAppUrl}/ns/v2/deeplook`,
        QUICK_ANALYTICS: `${config.northspyreProjectsAppUrl}/ns/v2/quick_analytics`,
        VENDOR_ANALYTICS: `${config.northspyreProjectsAppUrl}/ns/v2/vendor_analytics`,
        DEVELOPER_FEE: `${config.northspyreProjectsAppUrl}/ns/v2/portfolio_developer_fee`,
        GLOBAL_COST_FORECAST: `${config.northspyreProjectsAppUrl}/ns/global_cost_forecast`,
        GLOBAL_COST_FORECAST_V2: `${config.northspyreProjectsAppUrl}/ns/v2/global_cost_forecast`,
        PORTFOLIO_CAPITAL_PLANNING: `${config.northspyreProjectsAppUrl}/ns/v2/portfolio_capital_planning`,
        PORTFOLIO_INVOICES: `${config.northspyreProjectsAppUrl}/ns/v2/portfolio_invoice_data_index`,
        PORTFOLIO_BUDGET_CATEGORIZATIONS: `${config.northspyreProjectsAppUrl}/ns/v2/portfolio_budget_categorizations`,
        PORTFOLIO_VENDORS: `${config.northspyreProjectsAppUrl}/ns/v2/portfolio_vendors`,
        VENDOR_DIRECTORY: `${config.northspyreProjectsAppUrl}/ns/v2/vendors`,
        COMPANIES: `${config.northspyreProjectsAppUrl}/ns/v2/crm/companies`,
        COMPANY_DETAILS: (companyId: number) => `${config.northspyreProjectsAppUrl}/ns/v2/crm/company/${companyId}/details`,
        CONTACTS: `${config.northspyreProjectsAppUrl}/ns/v2/crm/contacts`,
        CONTACT_DETAILS: (contactId: number) => `${config.northspyreProjectsAppUrl}/ns/v2/crm/contact/${contactId}/details`,
        PROJECT_DASHBOARD_V2: (subprojectId: number) => `${config.northspyreProjectsAppUrl}/ns/v2/projects/project_overview/${subprojectId}`,
        DOCUMENT_LIBRARY: (
            subprojectId: number,
            directory_id?: number,
            asset_id?: number,
        ) => {
            const baseUrl = `${config.northspyreProjectsAppUrl}/ns/v2/documents/${subprojectId}`;
            const params = new URLSearchParams();
            if (directory_id) params.append('directory', String(directory_id));
            if (asset_id) params.append('asset', String(asset_id));
            return `${baseUrl}${params.toString() ? `?${params.toString()}` : ''}`;
        },
        PROJECT_SETUP: `${config.northspyreProjectsAppUrl}/ns/v2/user/project_setup`,
    },
};

export const STATIC_URLS = {
    TERMS_AND_CONDITIONS: 'https://www.northspyre.com/terms-and-conditions-deal',
    PRIVACY_POLICY: 'https://www.northspyre.com/privacypolicy',
    SUPPORT: 'https://knowledge.northspyre.com/knowledge',
    FEEDBACK_DEAL: 'https://northspyre.canny.io/deal-feature-requests',
};

const API_ENDPOINTS = {
    BASE_URL,
    AUTHENTICATED_USER: () => `${V1}/users/me`,
    FEATURE_ACCESS: (organizationId: string) => `${V1}/organizations/${organizationId}/user_auth_data`,
    DEALS: (organizationId: string) => `${V1}/${organizationId}/deals`,
    DEALS_FILTER_OPTIONS: (organizationId: string) => `${V1}/${organizationId}/deals/filter-options`,
    DEALS_LITE: (organizationId: string) => `${V1}/${organizationId}/deals/lite`,
    DEAL_FROM_SIGNUP: (organizationId: string) => `${V1}/${organizationId}/deals/from_signup`,
    DEAL_SUMMARIES: (organizationId: string) => `${V1}/${organizationId}/deals/summaries`,
    DEALS_BY_ID: (organizationId: string, dealId: number) => `${V1}/${organizationId}/deals/${dealId}`,
    DEAL_ORGANIZATION: (dealId: number) => `${V1}/deals/${dealId}/organization`,
    DEALS_GROUPS: (organizationId: string) => `${V1}/${organizationId}/deal-groups`,
    DEALS_GROUPS_BY_ID: (organizationId: string, dealGroupId: number) => `${V1}/${organizationId}/deal-groups/${dealGroupId}`,
    DEAL_OVERVIEW_PDF: (organizationId: string, dealId: number) => `${V1}/${organizationId}/deals/${dealId}/overview/pdf`,
    DEAL_DIRECTORY: {
        TREE: (dealId: number) => `${V1}/deals/${dealId}/directories/tree`,
        DIRECTORY: (dealId: number, directoryId?: number) => `${V1}/deals/${dealId}/directories${directoryId ? `/${directoryId}` : ''}`,
    },
    DEAL_DOCUMENTS: {
        BULK: () => `${V1}/documents/bulk`,
        DOCUMENTS: `${V1}/documents`,
        DOCUMENT_BY_ID: (documentId: number) => `${V1}/documents/${documentId}`,
        AI_SUGGESTIONS: (dealId: number) => `${V1}/documents/${dealId}/ai-suggestions`,
    },
    ASSOCIATE_DEALS_GROUPS: (organizationId: string) => `${V1}/${organizationId}/deal-groups/associate`,
    DISSOCIATE_DEALS_GROUPS: (organizationId: string) => `${V1}/${organizationId}/deal-groups/dissociate`,
    ORG_REPORTS: (orgId: string) => `${V1}/${orgId}/reports`,
    PROJECT_BUDGET_SCENARIOS: (projectUuid: string) => `${V1}/subproject/${projectUuid}/budget-scenarios`,
    REPORTS: {
        REPORT: (reportId: number) => `${V1}/reports/${reportId}`,
        PDF: (reportId: number) => `${V1}/reports/${reportId}/pdf`,
        PREVIEW: (reportId: number) => `${V1}/reports/${reportId}/preview`,
        SHOW_CREATE_COMBINED_REPORT: (orgId: string) => `${V1}/reports/${orgId}/show_create_combined_report`,
    },
    REPORT_BUILDER: {
        PDF: (dealId: number) => `${V1}/deals/${dealId}/report_builder/pdf`,
    },
    CRM: {
        DEAL_PROJECTS: (orgId: string, dealId: number) => `${V1}/organization/${orgId}/crm/deal/${dealId}/projects`,
        DEAL_COMPANIES: (orgId: string, dealId: number) => `${V1}/organization/${orgId}/crm/deal/${dealId}/companies`,
        DEAL_CONTACTS: (orgId: string, dealId: number) => `${V1}/organization/${orgId}/crm/deal/${dealId}/contacts`,
        DEAL_ACTIVITIES: (orgId: string, dealId: number) => `${V1}/organization/${orgId}/crm/deal/${dealId}/activities`,
        ACTIVITY: (orgId: string, activityId: number) => `${V1}/organization/${orgId}/crm/activities/${activityId}`,
        DEAL_NOTES: (orgId: string, dealId: number) => `${V1}/organization/${orgId}/crm/deal/${dealId}/activities/notes`,
        PROJECT_ASSOCIATIONS: (orgId: string) => `${V1}/organization/${orgId}/crm/project/associations`,
        COMPANY_ASSOCIATIONS: (orgId: string) => `${V1}/organization/${orgId}/crm/company/associations`,
        CONTACT_ASSOCIATIONS: (orgId: string) => `${V1}/organization/${orgId}/crm/contact/associations`,
        INTEGRATIONS: (orgId: string) => `${V1}/organization/${orgId}/crm/integrations`,
    },
    REPORT_BUILDER_TEMPLATE: (dealId: number) => `${V1}/deals/${dealId}/report_builder_templates`,
    TIMELINES: (orgId: string, dealId: number) => `${V1}/${orgId}/deals/${dealId}/timelines`,
    TIMELINE_BY_ID: (orgId: string, dealId: number, timelineId: string) => `${V1}/${orgId}/deals/${dealId}/timelines/${timelineId}`,
    TIMELINE_DUPLICATE: (orgId: string, dealId: number, timelineId: string) => `${V1}/${orgId}/deals/${dealId}/timelines/${timelineId}/duplicate`,
    TIMELINE_ASSIGNEES: (orgId: string, dealId: number, timelineId: number) => `${V1}/${orgId}/deals/${dealId}/timelines/${timelineId}/assignees`,
    TIMELINE_ASSIGNEE_BY_USER: (orgId: string, dealId: number, timelineId: number, userCorrelationId: string) => `${V1}/${orgId}/deals/${dealId}/timelines/${timelineId}/assignees/${userCorrelationId}`,
    TIMELINE_COMMENTS: (orgId: string, dealId: number, timelineId: number) => `${V1}/${orgId}/deals/${dealId}/timelines/${timelineId}/comments`,
    TIMELINE_LATEST_COMMENTS: (orgId: string, dealId: number) => `${V1}/${orgId}/deals/${dealId}/timelines/latest-comments`,
    COMMENT_BY_ID: (orgId: string, dealId: number, commentId: number) => `${V1}/${orgId}/deals/${dealId}/comments/${commentId}`,
    TIMELINE_DOCUMENTS: (orgId: string, dealId: number, timelineId: number) => `${V1}/${orgId}/deals/${dealId}/timelines/${timelineId}/documents`,
    TIMELINE_DOCUMENT_BY_ID: (orgId: string, dealId: number, timelineId: number, documentId: number) => `${V1}/${orgId}/deals/${dealId}/timelines/${timelineId}/documents/${documentId}`,
    TIMELINE_SECTIONS: (orgId: string, dealId: number) => `${V1}/${orgId}/deals/${dealId}/timeline-sections`,
    TIMELINE_SECTION_BY_ID: (orgId: string, dealId: number, sectionId: number) => `${V1}/${orgId}/deals/${dealId}/timeline-sections/${sectionId}`,
    BACK_OF_ENVELOPE: (orgId: string) => `${V1}/${orgId}/back-of-envelope`,
    BACK_OF_ENVELOPE_CALCULATE: `${V1}/public/boe/calculate`,
    BACK_OF_ENVELOPE_CALCULATE_RESIDUAL: `${V1}/public/boe/calculate-residual`,
    BACK_OF_ENVELOPE_DEALS: (orgId: string) => `${V1}/${orgId}/back-of-envelope/deals`,
    BACK_OF_ENVELOPE_SCENARIOS: (orgId: string) => `${V1}/${orgId}/back-of-envelope/scenarios`,
    BOE_SCENARIO_COMP_PDF: (orgId: string, dealId: number) => `${V1}/${orgId}/back-of-envelope/${dealId}/comparison/pdf`,
    BOE_SCENARIO_DASHBOARD_PDF: (orgId: string, scenarioId: number) => `${V1}/${orgId}/back-of-envelope/${scenarioId}/dashboard/pdf`,
    BACK_OF_ENVELOPE_BY_ID: (orgId: string, scenarioId: string) => `${V1}/${orgId}/back-of-envelope/${scenarioId}`,
    PRO_FORMA: (dealId: number, proFormaId?: number) => `${V1}/deals/${dealId}/pro_formas${proFormaId ? `/${proFormaId}` : ''}`,
    PRO_FORMA_ACTUALS: (dealId: number, proFormaId: number, organizationId: string) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/actuals?organizationId=${organizationId}`,
    PRO_FORMA_ROOM: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/room`,
    PRO_FORMA_MODELS: `${V1}/pro_forma_models`,
    ORGANIZATION_PRO_FORMA: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}`,
    DUPLICATE_PRO_FORMA: (organizationId: string, proFormaId: number) => `${V1}/organizations/${organizationId}/pro_formas/${proFormaId}/duplicate`,
    PRO_FORMA_SOURCE: (organizationId: string, proFormaId: string) => `${V1}/organization/${organizationId}/pro_forma/${proFormaId}/pro_forma_source`,
    PRO_FORMA_EXECUTIVE_SUMMARY: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/executive_summary`,
    PRO_FORMA_EXECUTIVE_SUMMARY_PDF: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/executive_summary/pdf`,
    PRO_FORMA_BUILDABLE_LOT: (
        dealId: number, proFormaId: number, lotId?: number,
    ) => `${V1}/deals/${dealId}/pro_forma/${proFormaId}/gross_buildable_area_lot${lotId ? `/${lotId}` : ''}`,
    PRO_FORMA_SENSITIVITY_ANALYSIS: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/sensitivity_analysis`,
    PRO_FORMA_SENSITIVITY_ANALYSIS_CREATE: (organizationId: string, proFormaId: number) => `${V1}/organizations/${organizationId}/pro_formas/${proFormaId}/sensitivity_analysis`,
    PRO_FORMA_SOURCES: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/sources`,
    PRO_FORMA_SUBPROJECT_LINK: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro-formas/${proFormaId}/subproject-link`,
    SIGNUP: {
        USER_AND_ORG: `${V1}/signup/user_and_org`,
        USER_AND_ORG_VALIDATE: `${V1}/signup/user_and_org/validate`,
        ORG_NAME_VALIDATE: (orgName: string) => `${V1}/signup/org_name/${orgName}/validate`,
        VALIDATE_SIGNUP: `${V1}/signup/validate_signup`,
        DEACTIVATED_USER_SUPPORT: `${V1}/signup/support/deactivated_user`,
        SEND_PASSWORD_RESET_EMAIL: `${V1}/signup/send_password_reset_email`,
    },
    UPLOADED_PRO_FORMAS: (organizationId: string, dealId: number) => `${V1}/${organizationId}/deals/${dealId}/uploaded_pro_formas`,
    UPLOADED_PRO_FORMAS_BY_ID: (organizationId: string, uploadedProFormaId: number) => `${V1}/${organizationId}/uploaded_pro_formas/${uploadedProFormaId}`,
    UPLOADED_PRO_FORMAS_BY_DEAL_AND_ID: (organizationId: string, dealId: number, uploadedProFormaId: number) => `${V1}/${organizationId}/deals/${dealId}/uploaded_pro_formas/${uploadedProFormaId}`,
    UPLOADED_PRO_FORMAS_EXTRACT: (organizationId: string, dealId: number) => `${V1}/${organizationId}/deals/${dealId}/uploaded_pro_formas/extract`,
    GENERATE_PDF: () => `${V1}/generate_pdf`,
    BUDGET_CLASSIFICATIONS: (
        organizationId: string, proFormaId: string,
    ) => `${V1}/organization/${organizationId}/pro_forma/${proFormaId}/budget_classifications`,
    DEFAULT_BUDGET_CLASSIFICATIONS: (
        organizationId: string, proFormaId: string,
    ) => `${V1}/organization/${organizationId}/pro_forma/${proFormaId}/budget_classifications/default`,
    BUDGET_CLASSIFICATION: (
        organizationId: string, proFormaId: string,
    ) => `${V1}/organization/${organizationId}/pro_forma/${proFormaId}/budget_classification`,
    USES_TABLE: (organizationId: string, proFormaId: string) => `${V1}/organizations/${organizationId}/pro_formas/${proFormaId}/uses`,
    SOURCES_TABLE: (organizationId: string, proFormaId: string) => `${V1}/organizations/${organizationId}/pro_formas/${proFormaId}/sources`,
    MAPBOX: (address: string, APIKey: string) => `https://api.mapbox.com/geocoding/v5/mapbox.places/${address}.json?access_token=${APIKey}&limit=1&country=us`,
    SOURCE_TYPES: `${V1}/source_types`,
    PROJECT_ASSETS: (projectUuid: string) => `${V1}/subproject/${projectUuid}/asset`,
    PROJECT_ASSETS_FROM_DEAL_DOCUMENTS: (projectUuid: string) => `${V1}/subproject/${projectUuid}/asset/from-deal-documents`,
    DEVELOPMENT_PROJECTS: (organizationUUid: string) => `${V1}/organization/${organizationUUid}/development-projects`,
    ASSET_TYPES: `${V1}/asset_types`,
    CONSTRUCTION_TYPES: `${V1}/construction_types`,
    MSAS: `${V1}/msas`,
    MSA_BY_ZIP_CODE: (zipCode: string) => `${V1}/msas/zip-code/${zipCode}`,
    USER_ORGANIZATIONS: `${V1}/user_organizations`,
    INVITE_USER: (organizationId: string) => `${V1}/organization/${organizationId}/invite_user`,
    ORGANIZATION: {
        LICENSES: (organizationId: string) => `${V1}/organization/${organizationId}/licenses`,
        ADD_ONS: (organizationId: string) => `${V1}/organization/${organizationId}/addons`,
        USERS: (organizationId: string) => `${V1}/organization/${organizationId}/users`,
        PROJECTS_BY_LICENSE_TIER: (organizationId: string) => `${V1}/organization/${organizationId}/license_tier/subproject`,
    },
    DEAL_ATTRIBUTES: (organizationId: string) => `${V1}/organizations/${organizationId}/deal-attributes`,
    ORGANIZATION_COMPANIES: (organizationId: string) => `${V1}/organizations/${organizationId}/companies`,
    ORGANIZATION_CONTACTS: (organizationId: string) => `${V1}/organizations/${organizationId}/contacts`,
    DROPDOWNS: `${V1}/dropdowns`,
    SUBPROJECT_STAGES: `${V1}/subproject_stages`,
    SUBPROJECT_STAGES_COUNT: (organizationId: string) => `${V1}/${organizationId}/deals/subproject_stages/count`,
    DEAL_SUBPROJECTS: (dealId: number) => `${V1}/deals/${dealId}/subprojects`,
    FILE_STORAGE: `${V1}/file_storage`,
    ORGANIZATION_SUBPROJECTS: (organizationId: string) => `${V1}/organization/${organizationId}/subprojects`,
    ORGANIZATION_USER_SUBPROJECTS: (organizationId: string) => `${V1}/organization/${organizationId}/user-subprojects`,
    ORGANIZATION_DEALS_WITH_PRO_FORMAS: (organizationId: string) => `${V1}/organization/${organizationId}/deals/pro_formas`,
    UPLOADED_PRO_FORMA: {
        PREVIEW: (organizationUuid: string, dealId: number) => `${V1}/${organizationUuid}/deals/${dealId}/uploaded_pro_forma/preview`,
        PRESIGNED_URL: (organizationUuid: string, dealId: number) => `${V1}/${organizationUuid}/deals/${dealId}/uploaded_pro_forma/presigned_url`,
        DELETE: (organizationUuid: string, uploadedProFormaId: number) => `${V1}/${organizationUuid}/uploaded_pro_formas/${uploadedProFormaId}`,
    },
    EXCEL_MAPPING: (organizationId: string) => `${V1}/${organizationId}/excel_mapping`,
    CREATE_SUBPROJECT_FROM_PRO_FORMA: (dealId: number, organizationId: string, proFormaId: number) => `${V1}/deals/${dealId}/subprojects/organization/${organizationId}/pro_forma/${proFormaId}/subproject`,
    DEALS_BULK_UPLOAD: {
        TEMPLATE: (organizationId: string) => `${V1}/${organizationId}/deals/bulk_upload/template`,
        PRESIGNED_URL: (organizationId: string) => `${V1}/${organizationId}/deals/bulk_upload/presigned_url`,
        PROCESS: (organizationId: string) => `${V1}/${organizationId}/deals/bulk`,
    },
    CLEAR_DATA_ON_LOGOUT: () => `${V1}/clear_data_on_logout`,
    INVESTMENT_MEMO: {
        GENERATE_DECK: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/investment_memo/generate_deck`,
        GENERATE_AI_CONTENT: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/investment_memo/generate_ai_content`,
        LATEST: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/investment_memo/latest`,
        MARK_VIEWED: (dealId: number, proFormaId: number) => `${V1}/deals/${dealId}/pro_formas/${proFormaId}/investment_memo/mark_viewed`,
    },
    TASKS: {
        TASK_STATUS: (taskId: string) => `${V1}/tasks/task_status/${taskId}`,
    },
};

export const QUERY_KEYS = {
    ASSET_TYPES: 'assetTypes',
    CONSTRUCTION_TYPES: 'constructionTypes',
    DEALS: 'deals',
    DEALS_LITE: 'dealsLite',
    MSAS: 'msas',
    ORGANIZATION: {
        LICENSES: 'organizationLicenses',
        ADD_ONS: 'organizationAddOns',
        USERS: 'organizationUsers',
        COMPANIES: 'organizationCompanies',
        PROJECTS_BY_LICENSE_TIER: 'organizationActiveProjectsByLicenseTier',
        CONTACTS: 'organizationContacts',
    },
    SIGNUP: {
        ORG_NAME_VALIDATE: 'signupOrgNameValidate',
        VALIDATE_SIGNUP: 'signupValidateSignup',
    },
    USER_ORGANIZATIONS: 'userOrganizations',
    DEAL_ATTRIBUTES: 'dealAttributes',
    DEAL_DIRECTORY_TREE: 'dealDirectoryTree',
    DOCUMENT_DETAIL: 'documentDetail',
    DOCUMENTS: 'documents',
    DROPDOWNS: 'dropdowns',
    PROJECT_ASSETS: 'projectAssets',
    DEAL_SUBPROJECTS: 'dealSubprojects',
    DEVELOPMENT_PROJECTS: 'developmentProjects',
    DOCUMENT_LIBRARY_DOCUMENTS: 'documentLibraryDocuments',
    PROJECT_ACTUALS: 'projectActuals',
    BOE: {
        SCENARIOS: 'boeScenarios',
        SCENARIO_COMPARISON_PDF: 'boeScenarioComparisonPdf',
    },
    REPORTS: {
        COMPARISON_REPORT: 'comparisonReport',
    },
    SUBPROJECT_STAGES: 'subprojectsStages',
    SUBPROJECT_STAGES_COUNT: 'subprojectStagesCount',
    DOCUMENT_AI_SUGGESTIONS: 'documentAiSuggestions',
    TIMELINE_COMMENTS: 'timelineComments',
    TIMELINE_LATEST_COMMENTS: 'timelineLatestComments',
    TIMELINE_DOCUMENTS: 'timelineDocuments',
};

export default API_ENDPOINTS;
